import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import { LastPathPersistence } from '@components/last-path-persistence';
import { Layout } from '@components/layout/layout';
import { ErrorBoundary } from '@containers/error-boundary';
import { AuthProvider } from '@contexts/auth/auth-provider';
import { CompanyAuthContextProvider } from '@contexts/company-auth-context/company-auth-provider';
import { CompanyClubProvider } from '@contexts/company-club/company-club-provider';
import { DarkModeProvider } from '@contexts/dark-mode/dark-mode-provider';
import { DevModeProvider } from '@contexts/dev-mode/dev-mode-provider';
import { FeatureFlagProvider } from '@contexts/feature-flag/feature-flag-provider';
import { FloorplanProvider } from '@contexts/floorplan/floorplan-provider';
import { GoogleTagManagerProvider } from '@contexts/google-tag-manager/google-tag-manager-provider';
import { LayoutProvider } from '@contexts/layout/layout-provider';
import { MagicSearchProvider } from '@contexts/magic-search/magic-search-provider';
import { ModalProvider } from '@contexts/modal/modal-provider';
import { EventNavigationProvider } from '@contexts/navigation/event-navigation-provider';
import { NavigationProvider } from '@contexts/navigation/navigation-provider';
import { ThemeProvider } from '@contexts/theme/theme-provider';
import { ToastProvider } from '@contexts/toast/toast-provider';
import { UserProvider } from '@contexts/user/user-provider';
import { WebviewBridgeProvider, WebviewReadySignal } from '@contexts/webview-bridge';
import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import moment from 'moment-timezone';

import { AppRoutes } from './app-routes';
import { QueryClientComponent } from './query-client';

const {
  VITE_VERSION,
  VITE_NAME,
  VITE_SENTRY_ENABLED,
  VITE_SENTRY_ENVIRONMENT,
  VITE_SENTRY_REPLAY_ERROR_SESSION_RATE: replayErrorRate,
  VITE_SENTRY_REPLAY_SESSION_RATE: replaySessionRate,
} = import.meta.env;

if (VITE_SENTRY_ENABLED === 'true') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
      }),
      // handled: false keeps console errors arriving as unhandled, which is what the alerting and
      // issue grouping already assume. The SDK flipped this default, so dropping it silently
      // reclassifies every console error we report.
      Sentry.captureConsoleIntegration({ levels: ['error'], handled: false }),
      Sentry.httpClientIntegration(),
    ],
    sendDefaultPii: true,
    replaysSessionSampleRate: Number(replaySessionRate),
    replaysOnErrorSampleRate: Number(replayErrorRate),
    release: `${VITE_NAME}@${VITE_VERSION}`,
    environment: VITE_SENTRY_ENVIRONMENT,
  });
}

const Application = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language;

  useEffect(() => {
    moment.locale(locale);
  }, [locale]);

  return (
    <ThemeProvider>
      <QueryClientComponent>
        <ErrorBoundary>
          <GoogleTagManagerProvider>
            <ToastProvider>
              <WebviewBridgeProvider>
                <WebviewReadySignal />
                <BrowserRouter basename={import.meta.env.BASE_URL}>
                  <UserProvider>
                    <FeatureFlagProvider>
                      <AuthProvider>
                        <CompanyAuthContextProvider>
                          <LayoutProvider>
                            <CompanyClubProvider>
                              <NavigationProvider>
                                <EventNavigationProvider>
                                  <FloorplanProvider>
                                    <DarkModeProvider>
                                      <DevModeProvider>
                                        <ModalProvider>
                                          <MagicSearchProvider>
                                            <LastPathPersistence />
                                            <Layout>
                                              <AppRoutes />
                                            </Layout>
                                          </MagicSearchProvider>
                                        </ModalProvider>
                                      </DevModeProvider>
                                    </DarkModeProvider>
                                  </FloorplanProvider>
                                </EventNavigationProvider>
                              </NavigationProvider>
                            </CompanyClubProvider>
                          </LayoutProvider>
                        </CompanyAuthContextProvider>
                      </AuthProvider>
                    </FeatureFlagProvider>
                  </UserProvider>
                </BrowserRouter>
                <ReactQueryDevtools initialIsOpen={false} />
              </WebviewBridgeProvider>
            </ToastProvider>
          </GoogleTagManagerProvider>
        </ErrorBoundary>
      </QueryClientComponent>
    </ThemeProvider>
  );
};

export const App = Sentry.withProfiler(Application);
